<template>
  <div class="app" :class="{ dark: isDark }">
    <header v-if="authStore.isAuthenticated" class="navbar">
      <div class="navbar-container">
        <router-link to="/" class="logo" aria-label="SlipSurface IQ">
          <BrandLogo />
        </router-link>

        <nav class="nav-links">
          <router-link to="/">{{ $t('Dashboard') }}</router-link>
          <router-link to="/pricing">{{ $t('Plans') }}</router-link>
          <span class="badge" :class="{ pro: authStore.isPro }">
            {{ planLabel }}
          </span>
        </nav>

        <div class="nav-actions">
          <button class="btn-icon lang" :title="$t('Language')" @click="setLang(lang === 'tr' ? 'en' : 'tr')">
            {{ lang === 'tr' ? 'EN' : 'TR' }}
          </button>
          <button @click="toggleDark" class="btn-icon" :title="$t('Toggle theme')">
            {{ isDark ? '☀️' : '🌙' }}
          </button>
          <button @click="logout" class="btn btn-secondary">{{ $t('Logout') }}</button>
        </div>
      </div>
    </header>

    <main class="main-content">
      <router-view />
    </main>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from './stores/auth'
import { lang, setLang, t } from './i18n'
import BrandLogo from './components/BrandLogo.vue'

const router = useRouter()
const authStore = useAuthStore()
// The theme follows the system until the person picks one; the pick is remembered. The class goes on
// <html> and <body> as well, and color-scheme tells the browser the page does its own dark mode, so it
// does not darken the text on its own and leave the cards white.
const stored = (() => { try { return localStorage.getItem('theme') } catch (err) { return null } })()
const prefersDark = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches
const isDark = ref(stored ? stored === 'dark' : prefersDark)
watch(isDark, (dark) => {
  document.documentElement.classList.toggle('dark', dark)
  document.body.classList.toggle('dark', dark)
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light'
}, { immediate: true })
const planLabel = computed(() => {
  const tier = authStore.user?.tier || ''
  return (tier === 'free' ? t('Free') : tier === 'pro_plus' ? 'Pro Plus' : tier).toLocaleUpperCase(lang.value)
})

const toggleDark = () => {
  isDark.value = !isDark.value
  try { localStorage.setItem('theme', isDark.value ? 'dark' : 'light') } catch (err) { /* private window */ }
}

const logout = () => {
  authStore.logout()
  router.push('/login')
}
</script>

<style scoped>
.app {
  min-height: 100vh;
  background-color: #fafafa;
  color: #333;
  transition: background-color 0.3s;
}

.app.dark {
  background-color: #1a1a1a;
  color: #e0e0e0;
}

.navbar {
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 0 20px;
  position: sticky;
  top: 0;
  z-index: 100;
}

.app.dark .navbar {
  background: #2a2a2a;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.navbar-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
}

.logo {
  font-size: 18px;
  font-weight: 600;
  text-decoration: none;
  color: #333;
}

.app.dark .logo {
  color: #fff;
}

.nav-links {
  display: flex;
  gap: 30px;
  align-items: center;
  flex: 1;
  margin-left: 40px;
}

.nav-links a {
  text-decoration: none;
  color: #666;
  font-size: 14px;
  transition: color 0.2s;
}

.app.dark .nav-links a {
  color: #aaa;
}

.nav-links a:hover {
  color: #333;
}

.app.dark .nav-links a:hover {
  color: #fff;
}

.badge {
  display: inline-block;
  padding: 4px 12px;
  background: #e0e0e0;
  color: #333;
  font-size: 12px;
  font-weight: 600;
  border-radius: 16px;
}

.badge.pro {
  background: #4CAF50;
  color: white;
}

.nav-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.btn-icon {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 4px;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.btn-icon.lang {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.btn-icon:hover {
  opacity: 1;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary {
  background: #f0f0f0;
  color: #333;
}

.app.dark .btn-secondary {
  background: #3a3a3a;
  color: #fff;
}

.btn:hover {
  opacity: 0.8;
}

.main-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}
</style>
